import { useEffect, useRef, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import AmbassadorProgramForm from '@/components/community/AmbassadorProgramForm';

import { Combobox } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScroll, useTransform } from 'framer-motion';
import kwesforms from 'kwesforms';

import AnimateWhileInView from '@/components/shared/AnimateWhileInView';
import BgEllipse from '@/components/shared/lp/BgEllipse';

import { siteData } from '@/data/configs/site';
import komodoAmbassador from '@/public/images/lp/community/ambassador.png';
import komodoCommunityCurrency from '@/public/images/lp/community/communityCurrency.png';
import discordCard from '@/public/images/lp/community/discord-card.png';
import komodoHandbook from '@/public/images/lp/community/handbook.png';
import komodoWorld from '@/public/images/lp/community/komodo-world.png';
import logoStairs from '@/public/images/lp/community/logo_stairs.png';
import redditCard from '@/public/images/lp/community/reddit-card.png';
import telegramCard from '@/public/images/lp/community/telegram-card.png';
import twitterCard from '@/public/images/lp/community/x-card.png';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';
import link from '@/public/images/lp/svgs/link.svg';
import checkbox from '@/public/img/community/applicationForm/checkbox.svg';
import discordIcon from '@/public/img/community/applicationForm/discord.svg';
import dropdown from '@/public/img/community/applicationForm/dropdown.svg';
import informationIcon from '@/public/img/community/applicationForm/info.svg';
import infoBg from '@/public/img/community/applicationForm/infoBg.svg';
import telegramIcon from '@/public/img/community/applicationForm/telegram.svg';
import discord1 from '@/public/img/community/socials/discord.svg';
import linkedin from '@/public/img/community/socials/linkedin.svg';
import messenger from '@/public/img/community/socials/messenger.svg';
import redditIcon from '@/public/img/community/socials/reddit.svg';
import telegram1 from '@/public/img/community/socials/telegram.svg';
import twitter1 from '@/public/img/community/socials/twitter.svg';
import twitterIcon from '@/public/img/community/socials/twitterIcon.svg';
import youtube from '@/public/img/community/socials/youtube.svg';

// const ref = useRef(null);

// useEffect(() => {
//   if (ref.current) {
//     const { scrollYProgress } = useScroll({
//       target: ref,
//       offset: ["start center", "end center"],
//     });
//     const opacity = useTransform(
//       scrollYProgress,
//       [0, 0.5, 1],
//       [0, 1, 0],
//     );

//     // You might want to use the opacity value here, for example:
//     // ref.current.style.opacity = opacity;
//   }
// }, [ref]);

const socialIcons = [
  {
    name: 'Discord',
    icon: discord1,
    link: siteData.social.discord,
  },
  {
    name: 'Telegram',
    icon: telegram1,
    link: siteData.social.telegram,
  },
  {
    name: 'X.com',
    icon: twitter1,
    link: siteData.social.twitter,
  },
  {
    name: 'Linkedin',
    icon: linkedin,
    link: siteData.social.linkedin,
  },
  {
    name: 'Youtube',
    icon: youtube,
    link: siteData.social.youtube,
  },
];

export default function Community() {


  // Create refs for scroll animations
  const headerRef = useRef(null);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const floatAnimation = {
    initial: { y: 0 },
    animate: {
      y: [-8, 8, -8],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      },
    },
  };

  // Animation variants for gradient ellipses
  const ellipseAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  };

  // Animation variants for community gradient
  const gradientAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.6,
        ease: 'easeOut',
      },
    },
  };



  return (
    <>
      <div suppressHydrationWarning className='absolute z-[-11] h-full w-full overflow-hidden'>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
          variants={gradientAnimation}
        >
          <ExportedImage
            placeholder='empty'
            alt='Community Gradient'
            src='/svg/gradients/community/community-gradient.svg'
            className='absolute left-0 top-[900px] h-auto w-[calc(1920px*0.65)] md:top-[800px] lg:top-[600px] lg:w-[calc(1920px*0.65)] xl:top-[600px] xl:w-[calc(1920px*0.75)] 2xl:top-[600px] 2xl:w-[calc(1920px*0.75)] 3xl:top-[600px] 3xl:w-[calc(1920px*0.85)]'
            height={1535}
            width={1581}
          />
        </motion.div>
      </div>
      <main suppressHydrationWarning className='relative z-[100] mx-auto font-sans text-white'>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-223 absolute left-[30%] top-[-200px] z-[-10] h-[359px] w-[287px] -translate-x-1/2 lg:left-[40%] lg:hidden -sm:top-[-200px]'
        ></motion.div>

        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='absolute bottom-[600px] left-[-100px] z-[-10] lg:h-[739px] lg:w-[755px] 3xl:-left-[300px] 3xl:bottom-[600px] 3xl:h-[1000px] 3xl:w-[1000px]'
          style={{
            borderRadius: '755px',
            opacity: 0.65,
            background: 'rgba(140, 140, 255, 0.59)',
            mixBlendMode: 'plus-lighter',
            filter: 'blur(139.85000610351562px)',
          }}
        ></motion.div>

        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='absolute bottom-[-200px] right-[-100px] z-[-10] lg:h-[508px] lg:w-[519px] 3xl:h-[910px] 3xl:w-[875px]'
          style={{
            borderRadius: '519px',
            opacity: 0.65,
            background: 'rgba(140, 140, 255, 0.59)',
            mixBlendMode: 'plus-lighter',
            filter: 'blur(139.85000610351562px)',
          }}
        ></motion.div>

        <div className='dummy hidden w-[50.21px] w-[70.21px] lg:w-[125.21px] lg:w-[90.21px]'></div>
        <div
          suppressHydrationWarning
          className='container items-center justify-center mx-auto md:grid md:grid-cols-24 md:gap-0'
        >
          <div className='hidden xl:col-span-2 xl:block'></div>
          <div className='relative items-center justify-center w-full px-4 mx-auto max-w-7xl xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
            <motion.section
              ref={headerRef}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className='flex flex-col items-center justify-center bg-[url(/svg/gradients/community/mesh.svg)] pt-24 md:pt-36 lg:flex-row lg:pt-36'
            >
              <div className='relative z-10 mx-auto w-full bg-black bg-opacity-10 text-center sm:max-w-[600px] lg:max-w-[800px]'>
                <motion.h1
                  variants={fadeInUp}
                  className='flex flex-col font-heading text-4xl font-semibold leading-tight tracking-[-2.88px] md:text-5xl md:leading-[3.5rem] md:tracking-[-2px] lg:text-6xl lg:leading-[4rem] lg:tracking-[-2.5px] xl:text-[80px] xl:leading-[110%] xl:tracking-[-3px] 2xl:text-[96px]'
                >
                  <span className='font-semibold text-transparent community-text'>
                    Community-first
                  </span>{' '}
                  <span className='font-normal'>Approach</span>
                </motion.h1>
                <motion.div
                  variants={fadeInUp}
                  className='event-centric-btn py-[15px] text-center font-sans text-xl font-normal md:py-[18px] md:text-2xl lg:py-[20px] lg:text-[28px] 2xl:text-[32px] 2xl:leading-[120%]'
                >
                  Collaborative Network
                </motion.div>
                <motion.div
                  variants={fadeInUp}
                  className='mx-auto mt-4 w-full text-center text-lg leading-[1.6] sm:w-[550px] md:text-xl md:leading-[1.7] lg:w-[625px] lg:text-[22px] lg:leading-[1.5]'
                >
                  Komodo is a community of hackers, developers, entrepreneurs, and enthusiasts
                  pushing the boundaries of blockchain technology.
                </motion.div>
              </div>
            </motion.section>

            <motion.section
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className='relative z-10 mx-auto mt-32 grid items-center gap-20 pb-[110px] lg:grid-cols-2'
            >
              <motion.div variants={fadeInUp} className=''>
                <div className='-lg:text-center'>
                  <h2 className='font-heading text-3xl font-semibold leading-tight tracking-[1px] md:text-3xl md:leading-[1.2] md:tracking-[1.5px] lg:text-4xl lg:leading-[1.25] lg:tracking-[2px] xl:text-[48px] xl:leading-[58px]'>
                    Why Komodo Crypto Community?
                  </h2>
                  <p className='pt-[15px] font-sans text-base leading-[1.5] text-br-semi-white md:text-[17px] lg:text-[18px] lg:leading-[1.6] 2xl:w-[634px]'>
                    Komodo&apos;s blockchain ecosystem is built by and for passionate technology
                    pioneers.
                  </p>
                  <p className='pb-6 pt-[11px] font-sans text-base leading-[1.5] text-br-semi-white md:text-[17px] lg:text-[18px] lg:leading-[1.6] 2xl:w-[634px]'>
                    Become part of our dynamic crypto community where innovative minds collaborate
                    to advance digital freedom and self-sovereignty.
                  </p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link
                      href='https://komodoplatform.com/discord'
                      target='_blank'
                      className='join-now-btn px-4 py-[11px] text-base font-medium text-white md:text-lg'
                    >
                      Join the Movement
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
              <motion.div variants={floatAnimation} initial='initial' animate='animate'>
                <ExportedImage
                  placeholder='empty'
                  alt='komodoWorld'
                  className='mx-auto h-auto w-[560px]'
                  src={komodoWorld}
                />
              </motion.div>
            </motion.section>
          </div>
          <div className='hidden xl:col-span-2 xl:block'></div>
        </div>
        <section className='relative flex flex-col items-center justify-center py-[30px] pb-[20px] lg:flex-row lg:gap-[10px] lg:pb-0 xl:gap-[60px]'>
          <motion.div
            initial={{ opacity: 0, x: -180, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.9,
              type: 'spring',
              stiffness: 90,
              damping: 20,
            }}
            className='flex h-[220px] w-[90%] flex-col justify-center rounded-[17.817px] backdrop-blur-[20.14px] xms:w-[80%] sm:w-[550px] lg:w-[500px] lg:pr-[65px] xl:w-[600px] 2xlm:h-[250px] 2xlm:w-[787px] 2xlm:py-[50px] 2xlm:pr-[89px] -lg:bg-opacity-10 -sm:px-2'
            style={{
              background:
                'linear-gradient(35deg, rgb(83, 109, 203) 0%, rgb(0, 0, 0) 50%) left bottom',
              backgroundPosition: 'bottom left',
              borderRadius: '17.817px',
              border: '0.848px solid #493db5',
            }}
          >
            <h2 className='text-center font-heading text-xl font-semibold leading-tight text-white md:text-[24px] lg:text-right lg:text-[28px] 2xl:text-[40px]'>
              Join the Best <br />
              Blockchain Community
            </h2>
            <p className='pt-[15px] text-center text-sm leading-[1.5] text-white sm:text-base sm:leading-[1.6] md:pt-[18px] lg:pt-[22px] lg:text-right lg:text-base lg:leading-[1.6]'>
              Be a Komodian. Contribute, chat and trade among Komodians.
              <br />
              Follow our socials to stay updated!
            </p>
          </motion.div>
          <div className='pt-10 lg:pt-0'>
            <motion.div
              initial={{ opacity: 0, x: 180, y: 0 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.9,
                type: 'spring',
                stiffness: 90,
                damping: 20,
              }}
              className='socials-bg relative mx-auto flex h-[150px] w-[90%] shrink-0 flex-row items-center justify-between p-8 py-[40px] xms:w-[80%] sm:h-[220px] sm:w-[550px] lg:w-[500px] xl:w-[600px] 2xlm:h-[250px] 2xlm:w-[787px] 2xlm:px-[25px] 2xlm:py-[25px]'
            >
              {socialIcons.map((socialIcon, index) => (
                <motion.div
                  key={`social-icon-${index}`}
                  whileHover={{ scale: 1.15, rotate: [0, -5, 5, 0], transition: { duration: 0.5 } }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Link href={socialIcon.link} target='_blank'>
                    <ExportedImage
                      src={socialIcon.icon}
                      alt={`social-icon-${index}`}
                      className='h-[50px] w-[50px] sm:h-[70px] sm:w-[70px] md:h-[90px] md:w-[90px]'
                    />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        <div
          suppressHydrationWarning
          className='container z-10 items-center justify-center mx-auto md:grid md:grid-cols-24 md:gap-0'
        >
          <div className='hidden xl:col-span-2 xl:block'></div>
          <div className='relative w-full max-w-7xl px-4 py-[47px] xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
            <motion.section
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className='relative z-10 items-center justify-center w-full h-auto py-6 mx-auto'
            >
              <ul className='mx-auto grid items-center justify-center gap-x-[20px] gap-y-[20px] overflow-hidden lg:grid-cols-2 lg:gap-y-[0px] xl:gap-x-[50px] 2xl:gap-x-[79px]'>
                <motion.li
                  variants={fadeInUp}
                  whileHover={{
                    y: -10,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    transition: { duration: 0.3 },
                  }}
                  key={'komodo-community-currency'}
                  className='community-cards-bg relative mx-auto flex w-[320px] flex-col items-center justify-center xs:w-[400px] sm:h-[550px] sm:w-[550px] lg:w-[450px] xl:h-[591px] xl:w-[540px]'
                  style={{
                    border: '0.848px solid #1d4b6c',
                  }}
                >
                  <div
                    className='relative w-full rounded-[17.817px] bg-opacity-50'
                    style={{
                      background:
                        'radial-gradient(53.51% 53.7% at 49.94% 50.16%, #2426B2 0%, #1E1138 100%)',
                    }}
                  >
                    <ExportedImage
                      placeholder='empty'
                      alt='komodoCommunityCurrency'
                      src={komodoCommunityCurrency}
                      className='mx-auto h-auto w-[75%]'
                    />
                  </div>
                  <div className='flex flex-1 flex-col justify-between px-10 pb-[20px] xl:pb-[42px]'>
                    <h2 className='pt-4 font-heading text-lg font-semibold text-white xs:text-3xl md:leading-[42px] lg:text-[30px] xl:text-2xl xl:text-[40px]'>
                      Komodo Community Cryptocurrency
                    </h2>
                    <p className='pt-2 font-sans text-base font-normal leading-[23px] sm:pt-3'>
                      $KMD can be traded on popular cryptocurrency exchanges and used for payments
                      at a variety of physical stores and online retailers.
                    </p>
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.3 }}>
                      <Link
                        href={'/en/wallet/'}
                        target='_blank'
                        className='block font-semibold text-transparent w-max'
                      >
                        <div className='flex flex-row items-center'>
                          <span className='font-bold text-transparent cards-btn'>
                            Browse Wallets
                          </span>
                          <ExportedImage
                            placeholder='empty'
                            src={gradientLinkArrow}
                            className='ml-2 h-[12px] w-auto'
                            width={12}
                            height={12}
                            alt='link'
                          />
                        </div>
                      </Link>
                    </motion.div>
                  </div>
                </motion.li>
                <motion.li
                  variants={fadeInUp}
                  whileHover={{
                    y: -10,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    transition: { duration: 0.3 },
                  }}
                  key={'komodo-handbook'}
                  className='community-cards-bg mx-auto flex w-[320px] flex-col items-center justify-center xs:w-[400px] sm:h-[550px] sm:w-[550px] lg:w-[450px] xl:h-[591px] xl:w-[540px]'
                  style={{
                    border: '0.848px solid #1d4b6c',
                  }}
                >
                  <div
                    className='relative w-full rounded-[17.817px]'
                    style={{
                      background:
                        'radial-gradient(53.51% 53.7% at 49.94% 50.16%, #2426B2 0%, #1E1138 100%)',
                    }}
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={komodoHandbook}
                      alt='komodoHandbook'
                      className='w-full h-auto'
                    />
                  </div>
                  <div className='flex flex-1 flex-col justify-between px-10 pb-[20px] xl:pb-[42px]'>
                    <h2 className='pt-4 font-heading text-lg font-semibold text-white xs:text-3xl md:leading-[42px] lg:text-[30px] xl:text-2xl xl:text-[40px]'>
                      Komodo Handbook
                    </h2>
                    <p className='pt-2 font-sans text-base font-normal leading-[23px] sm:pt-3'>
                      Learn everything you need to know about Komodo&apos;s technology and how you
                      can get involved with the community.
                    </p>
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.3 }}>
                      <Link
                        href={'https://komodoplatform.com/en/blog/komodo-handbook/'}
                        target='_blank'
                        className='block font-semibold text-transparent w-max'
                      >
                        <div className='flex flex-row items-center'>
                          <span className='font-bold text-transparent cards-btn'>Read Now</span>
                          <ExportedImage
                            placeholder='empty'
                            src={gradientLinkArrow}
                            className='ml-2 h-[12px] w-auto'
                            width={12}
                            height={12}
                            alt='link'
                          />
                        </div>
                      </Link>
                    </motion.div>
                  </div>
                </motion.li>
              </ul>
            </motion.section>
          </div>
          <div className='hidden xl:col-span-2 xl:block'></div>
        </div>
        {/* <AmbassadorProgramForm /> */}

      </main>
    </>
  );
}
